import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { api, ApiError } from "../api/client";
import { Panel } from "../components/Panel";
import { PlanogramPanel } from "../components/PlanogramPanel";
import { StaffPanel } from "../components/StaffPanel";
import { TracksPanel } from "../components/TracksPanel";
import { useFilters } from "../lib/filters";
import { ui } from "../lib/ui";
import { useTransientStatus } from "../lib/useTransientStatus";
import type { Branch } from "../types";

type View = "plan" | "staff" | "tracks" | "";

const VIEWS: { key: View; label: string }[] = [
  { key: "plan", label: "Floor plan & cameras" },
  { key: "staff", label: "Staff" },
  { key: "tracks", label: "Tracks" },
];

const isView = (v: string | null): v is View =>
  v === "plan" || v === "staff" || v === "tracks" || v === "";

const FORM_GRID = "grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3.5";

/** This store — the one branch this PC is. Its floor plan and entrance
 *  cameras, its enrolled staff, its tracks, and the location details the hub
 *  gave it (editable here for a PC without a hub). There is no list of other
 *  branches on a branch PC. */
export function Store() {
  const { branches, branchesError, reloadBranches, defaultBranch } = useFilters();
  const branch = defaultBranch ? branches.find((b) => b.id === defaultBranch.id) ?? null : null;

  const [params, setParams] = useSearchParams();
  const raw = params.get("view");
  const view: View = isView(raw) ? raw : "plan";
  const setView = (next: View) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === "plan") p.delete("view");
        else p.set("view", next);
        // Sub-state of the panel that is closing.
        if (next !== "plan") p.delete("rack");
        if (next !== "tracks") {
          p.delete("track_day");
          p.delete("track_camera");
          p.delete("track_page");
        }
        return p;
      },
      { replace: true },
    );

  // The location form: a draft of the branch row, re-seeded when the row
  // changes (a sync from the hub, a save) but never while typing.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Branch | null>(null);
  const [status, setStatus] = useTransientStatus();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!editing) setDraft(branch ? { ...branch } : null);
  }, [branch, editing]);

  const save = async () => {
    if (!draft) return;
    setError(null);
    try {
      const {
        id: _id,
        cameras: _cameras,
        camera_dirs: _cameraDirs,
        is_demo: _demo,
        ...patch
      } = draft;
      await api.patchBranch(draft.id, patch);
      setStatus("Saved");
      setEditing(false);
      reloadBranches();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  if (branchesError) return <p className={`${ui.notice} ${ui.noticeError}`}>{branchesError}</p>;
  if (!defaultBranch) {
    return (
      <p className={`${ui.notice} ${ui.noticeError}`}>
        This PC has no store yet. Open the Settings menu (the gear in the header) › Switch
        store › Add new store: paste the store&apos;s hub token, or name a local store.
      </p>
    );
  }
  if (!branch) {
    return branches.length === 0 ? (
      <p className={`${ui.notice} text-muted`}>Loading…</p>
    ) : (
      <p className={`${ui.notice} ${ui.noticeError}`}>
        Branch {defaultBranch.id} is not in this PC&apos;s database.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <Panel
        title={branch.name}
        subtitle={`${branch.governorate}${branch.area ? ` › ${branch.area}` : ""}${branch.address ? ` · ${branch.address}` : ""} · ${branch.id}`}
        actions={
          <div className="flex flex-wrap gap-1.5">
            {VIEWS.map((v) => (
              <button
                key={v.key}
                type="button"
                className={`${ui.btnQuiet} ${view === v.key ? ui.btnSelected : ""}`}
                onClick={() => setView(view === v.key ? "" : v.key)}
              >
                {v.label}
              </button>
            ))}
            <button type="button" className={ui.btnQuiet} onClick={() => setEditing((v) => !v)}>
              {editing ? "Cancel" : "Edit location"}
            </button>
          </div>
        }
      >
        {status && <p className={`${ui.notice} ${ui.noticeSuccess}`}>{status}</p>}
        {error && <p className={`${ui.notice} ${ui.noticeError}`}>{error}</p>}
        {editing && draft ? (
          <>
            <div className={FORM_GRID}>
              <label className={ui.field}>
                Name
                <input className={ui.input} value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              </label>
              <label className={ui.field}>
                Governorate
                <input className={ui.input} value={draft.governorate}
                  onChange={(e) => setDraft({ ...draft, governorate: e.target.value })} />
              </label>
              <label className={ui.field}>
                Area
                <input className={ui.input} value={draft.area}
                  onChange={(e) => setDraft({ ...draft, area: e.target.value })} />
              </label>
              <label className={`${ui.field} sm:col-span-2`}>
                Address
                <input className={ui.input} value={draft.address}
                  onChange={(e) => setDraft({ ...draft, address: e.target.value })} />
              </label>
              <label className={ui.field}>
                Timezone
                <input className={ui.input} value={draft.timezone}
                  onChange={(e) => setDraft({ ...draft, timezone: e.target.value })} />
              </label>
              <label className={`${ui.field} sm:col-span-2`}>
                Footage directory override (optional)
                <input className={`${ui.input} font-mono text-xs`} value={draft.video_dir ?? ""}
                  placeholder={`Default: <footage root>/${draft.id}`}
                  onChange={(e) =>
                    setDraft({ ...draft, video_dir: e.target.value.trim() === "" ? null : e.target.value })
                  } />
              </label>
              <label className={`${ui.field} flex-row items-center gap-2 font-medium`}>
                <input type="checkbox" checked={draft.active}
                  onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
                Active
              </label>
            </div>
            <p className="mt-2 text-xs text-muted">
              On a PC connected to the hub, the name and place are the hub&apos;s and are
              refreshed from it on every start; the footage override and the cameras are
              this PC&apos;s.
            </p>
            <div className="mt-[18px] flex gap-2">
              <button type="button" className={ui.btn} onClick={save}>Save changes</button>
              <button type="button" className={ui.btnQuiet} onClick={() => setEditing(false)}>Cancel</button>
            </div>
          </>
        ) : (
          <p className="text-[13px] text-muted">
            {Object.keys(branch.cameras).length === 0
              ? "No entrance camera yet — open Floor plan & cameras, place the camera on the plan and set it up with a sample clip to draw the entry line."
              : `${Object.keys(branch.cameras).length} entrance camera(s) set up: ${Object.keys(branch.cameras).join(", ")}.`}
          </p>
        )}
      </Panel>

      {view === "plan" && (
        <PlanogramPanel key={branch.id} branch={branch} onClose={() => setView("")}
          onEditLocation={() => setEditing(true)} />
      )}
      {view === "staff" && (
        <StaffPanel key={branch.id} branch={branch} onClose={() => setView("")} />
      )}
      {view === "tracks" && (
        <TracksPanel key={branch.id} branch={branch} onClose={() => setView("")} />
      )}
    </div>
  );
}
